import React, { useState } from "react";
import { DEFAULT_TITLE_COLOR } from "../utils/titleColors";

const FOLDER_DRAG_MIME = "application/x-addanote-folder";

function readDraggedFolderPayload(event) {
  const rawPayload = event.dataTransfer?.getData(FOLDER_DRAG_MIME)
    || event.dataTransfer?.getData("text/plain")
    || "";
  if (!rawPayload) return null;

  try {
    const parsed = JSON.parse(rawPayload);
    if (parsed?.type !== "folder" || !parsed?.sourceGroupId || !parsed?.sourceFolderPath) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className="menu-icon-svg">
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export default function Menu({
  user,
  total,
  insights,
  activeView,
  onLogout,
  setActiveView,
  onGoHome,
  titles = [],
  selectedGroupId = "",
  expandedGroupId = "",
  sidebarChildrenByGroup = {},
  loadingGroupId = "",
  isCollapsed = false,
  isMobile = false,
  defaultTitlesExpanded = false,
  onToggleCollapsed,
  onToggleGroup,
  onOpenChild,
  onCreateNote,
  onDropFolder,
}) {
  const [titlesExpanded, setTitlesExpanded] = useState(defaultTitlesExpanded);
  const [insightsExpanded, setInsightsExpanded] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [folderDropTarget, setFolderDropTarget] = useState("");
  const toggleTitlesExpanded = () => {
    setProfileMenuOpen(false);
    setTitlesExpanded((current) => !current);
  };
  const toggleInsightsExpanded = () => {
    setProfileMenuOpen(false);
    setInsightsExpanded((current) => !current);
  };
  const navItems = [
    { id: "dashboard", label: "Dashboard" },
    { id: "titles", label: "Titles" },
    { id: "learning", label: "Learning" },
    { id: "calendar", label: "Calendar" },
    { id: "flights", label: "Flights" },
    { id: "new", label: "New note" },
    { id: "profile", label: "Profile" },
  ];
  if (user?.isAdmin) {
    navItems.splice(2, 0, { id: "generator", label: "Generator" });
  }
  const quickTitleLinks = titles.slice(0, 6);
  const showSidebarDetailPanels = activeView !== "titles";
  const insightItems = [
    {
      label: "Total Titles",
      value: `${insights?.totalTitles ?? total}`,
      detail: "Top-level containers holding one or more notes.",
    },
    {
      label: "Current Page",
      value: `${insights?.currentPage ?? 1} / ${insights?.totalPages ?? 1}`,
      detail: "Browse titles without losing the overview.",
    },
    {
      label: "Active Filter",
      value: insights?.activeFilter || "None",
      detail: insights?.activeFilter
        ? "Matching both titles and child note content."
        : "Showing all titles.",
    },
  ];

  const handleFolderDrop = (event, group, targetFolderPath = "", targetKey = "") => {
    const payload = readDraggedFolderPayload(event);
    if (!payload) return;
    event.preventDefault();
    event.stopPropagation();
    setFolderDropTarget("");
    onDropFolder?.(payload, group, targetFolderPath);
  };

  const handleFolderDragOver = (event, targetKey) => {
    const payload = readDraggedFolderPayload(event);
    if (!payload) return;
    event.preventDefault();
    if (folderDropTarget !== targetKey) {
      setFolderDropTarget(targetKey);
    }
  };

  return (
    <div className={`menu-shell ${isCollapsed ? "is-collapsed" : ""}`}>
      <div className="menu-shell-topbar">
        <span className="menu-topbar-label">Menu</span>
        <button
          type="button"
          className="menu-collapse-button"
          onClick={() => {
            setProfileMenuOpen(false);
            onToggleCollapsed?.();
          }}
          aria-label={isCollapsed ? "Show sidebar" : "Hide sidebar"}
          aria-expanded={!isCollapsed}
        >
          <span className="icon-button-content">
            <HamburgerIcon />
          </span>
        </button>
      </div>

      {isCollapsed ? null : (
        <>
      <div className="menu-user-card-shell">
        <button
          type="button"
          className="menu-user-card menu-user-card-button"
          onClick={() => setProfileMenuOpen((current) => !current)}
          aria-haspopup="menu"
          aria-expanded={profileMenuOpen}
        >
          <div className="menu-user-avatar">{(user?.name || user?.email || "U").slice(0, 1).toUpperCase()}</div>
          <div>
            <div className="menu-user-heading">
              <strong>{user?.name || "Signed in user"}</strong>
              {user?.isAdmin ? <span className="user-role-badge">Admin</span> : null}
            </div>
            <div className="text-muted small menu-account-email" title={user?.email}>{user?.email}</div>
          </div>
        </button>
        {profileMenuOpen ? (
          <div className="menu-user-dropdown" role="menu" aria-label="Profile menu">
            <button
              type="button"
              className="menu-user-dropdown-link"
              role="menuitem"
              onClick={() => {
                setProfileMenuOpen(false);
                setActiveView("profile");
              }}
            >
              Profile Settings
            </button>
            <button
              type="button"
              className="menu-user-dropdown-link danger"
              role="menuitem"
              onClick={onLogout}
            >
              Sign Out
            </button>
          </div>
        ) : null}
      </div>

      <nav aria-label="Main menu" className={`menu-nav ${isMobile ? "mobile-nav-menu" : ""}`}>
        {navItems.map((item) => (
          <button
            key={item.id}
            className={`menu-link ${isMobile ? "mobile-nav-link" : ""} ${activeView === item.id ? "is-active" : ""}`}
            onClick={() => {
              setProfileMenuOpen(false);
              setActiveView(item.id);
            }}
          >
            <span>{item.label}</span>
          </button>
        ))}
      </nav>

      {showSidebarDetailPanels ? (
      <div className="menu-child-panel">
        <div className="menu-child-heading">
          <button
            type="button"
            className="eyebrow menu-child-heading-toggle"
            onClick={toggleTitlesExpanded}
            aria-expanded={titlesExpanded}
            aria-controls="menu-quick-titles"
          >
            <span>Titles</span>
          </button>
        </div>

        {!titlesExpanded ? null : !quickTitleLinks.length ? (
          <p className="menu-child-empty">Open Titles to load note containers here for quick access.</p>
        ) : (
          <div id="menu-quick-titles" className="menu-child-list" aria-label="Quick title links">
            {quickTitleLinks.map((group) => (
              <div key={group.id} className="menu-title-group">
                <button
                  type="button"
                  className={`menu-child-link ${selectedGroupId === group.id ? "is-active" : ""} ${folderDropTarget === `group:${group.id}` ? "is-drop-target" : ""}`}
                  onClick={() => {
                    setProfileMenuOpen(false);
                    onToggleGroup?.(group);
                  }}
                  aria-expanded={expandedGroupId === group.id}
                  onDragOver={(event) => handleFolderDragOver(event, `group:${group.id}`)}
                  onDragLeave={() => setFolderDropTarget((current) => current === `group:${group.id}` ? "" : current)}
                  onDrop={(event) => handleFolderDrop(event, group, "", `group:${group.id}`)}
                >
                  <div>
                    <strong style={{ color: group.color || DEFAULT_TITLE_COLOR }}>{group.title || "Untitled"}</strong>
                    <span>{group.note_count || 0} note{group.note_count === 1 ? "" : "s"}</span>
                  </div>
                  <span className="menu-child-link-indicator" aria-hidden="true">
                    {expandedGroupId === group.id ? "−" : "+"}
                  </span>
                </button>

                {expandedGroupId === group.id ? (
                  <div className="menu-grandchild-list">
                    {loadingGroupId === group.id ? (
                      <div className="menu-grandchild-empty">Loading children...</div>
                    ) : (sidebarChildrenByGroup[group.id] || []).length ? (
                      (sidebarChildrenByGroup[group.id] || []).map((entry) => (
                        <button
                          key={entry.id}
                          type="button"
                          className={`menu-grandchild-link ${folderDropTarget === `entry:${entry.id}` ? "is-drop-target" : ""}`}
                          onClick={() => {
                            setProfileMenuOpen(false);
                            onOpenChild?.(group, entry);
                          }}
                          onDragOver={(event) => handleFolderDragOver(event, `entry:${entry.id}`)}
                          onDragLeave={() => setFolderDropTarget((current) => current === `entry:${entry.id}` ? "" : current)}
                          onDrop={(event) => handleFolderDrop(
                            event,
                            group,
                            entry.folder_path ? `${entry.folder_path}/${entry.title}` : String(entry.title || "").trim(),
                            `entry:${entry.id}`
                          )}
                        >
                          <strong>{entry.title || "Untitled note"}</strong>
                          <span>{entry.folder_path || "Root level"}</span>
                        </button>
                      ))
                    ) : (
                      <div className="menu-grandchild-empty">No child notes inside this title yet.</div>
                    )}
                  </div>
                ) : null}
              </div>
            ))}
            <button
              type="button"
              className="action-link menu-child-view-all"
              onClick={() => {
                setProfileMenuOpen(false);
                setActiveView("titles");
              }}
            >
              View all
            </button>
          </div>
        )}
      </div>
      ) : null}

      {showSidebarDetailPanels ? (
      <div className="menu-summary-card">
        <span className="eyebrow">Snapshot</span>
        <strong>{total} saved title{total === 1 ? "" : "s"}</strong>
        <p>Dashboard stays focused on overview, while titles and note creation now have dedicated screens.</p>
        <div className="menu-shortcut-hint" aria-label="Keyboard shortcuts">
          <span>Shortcuts</span>
          <code>M</code>
          <span>menu</span>
          <code>Esc</code>
          <span>close</span>
        </div>
      </div>
      ) : null}

      {showSidebarDetailPanels ? (
      <div className="menu-child-panel menu-insights-panel">
        <div className="menu-child-heading">
          <button
            type="button"
            className="eyebrow menu-child-heading-toggle"
            onClick={toggleInsightsExpanded}
            aria-expanded={insightsExpanded}
            aria-controls="menu-library-summary"
          >
            <span>Library Summary</span>
          </button>
        </div>

        {!insightsExpanded ? null : (
          <div id="menu-library-summary" className="menu-insights-list" aria-label="Library summary">
            {insightItems.map((item) => (
              <article key={item.label} className="menu-insight-card">
                <span className="stat-label">{item.label}</span>
                <strong>{item.value}</strong>
                <p>{item.detail}</p>
              </article>
            ))}
          </div>
        )}
      </div>
      ) : null}

        </>
      )}
    </div>
  );
}
